import { LoginFormCopy } from "@bluebottlecoffee/design-system";
import { ConsentManagerCopyProps } from "../../components/ConsentManagerWrapper";

import {
  AnnouncementBar,
  BrewGuide,
  BrewGuidesPage,
  Cafe,
  CafesPage,
  Cart,
  CoffeeFulfillmentCutOff,
  CoffeeFulfillmentHolidays,
  Config,
  CurrentSubscriptionsPage,
  EmailOptIn,
  ErrorPage,
  Footer,
  HelpPage,
  Homepage,
  InstantEspresso,
  Nav,
  OurCoffee,
  OurStory,
  OurValues,
  PrivacyPage,
  Product,
  ProductRec,
  ShopCollection,
  ShopPage,
  SubscriptionCancellation,
  SubscriptionPage,
  Sustainability,
  TermsOfUsePage,
  TranslationGroup,
} from "../sanity-schema";
import { buildTranslationGroupQuery } from "./translation-group-queries";

// Please maintain alphabetical order
type DocumentType =
  | AnnouncementBar["_type"]
  | BrewGuide["_type"]
  | BrewGuidesPage["_type"]
  | Cafe["_type"]
  | CafesPage["_type"]
  | Cart["_type"]
  | CoffeeFulfillmentCutOff["_type"]
  | CoffeeFulfillmentHolidays["_type"]
  | Config["_type"]
  | CurrentSubscriptionsPage["_type"]
  | EmailOptIn["_type"]
  | ErrorPage["_type"]
  | Footer["_type"]
  | Homepage["_type"]
  | HelpPage["_type"]
  | InstantEspresso["_type"]
  | Nav["_type"]
  | OurCoffee["_type"]
  | OurStory["_type"]
  | OurValues["_type"]
  | PrivacyPage["_type"]
  | Product["_type"]
  | ProductRec["_type"]
  | ShopCollection["_type"]
  | ShopPage["_type"]
  | SubscriptionCancellation["_type"]
  | SubscriptionPage["_type"]
  | Sustainability["_type"]
  | TermsOfUsePage["_type"]
  | TranslationGroup["_type"];

export enum SharedQueryKey {
  ANNOUNCEMENT_BAR = "announcementBar",
  CART = "cart",
  CONSENT_MANAGER = "consentManagerCopy",
  FOOTER = "footer",
  LOGIN_DIALOG = "loginDialogCopy",
  NAV = "nav",
  EMAIL_OPT_IN = "emailOptIn",
}

export interface PageLink {
  name: string;
  slug: string;
}

export interface BaseSanityQueryType {
  [SharedQueryKey.ANNOUNCEMENT_BAR]: AnnouncementBar;
  [SharedQueryKey.CONSENT_MANAGER]: ConsentManagerCopyProps;
  [SharedQueryKey.FOOTER]: Footer;
  [SharedQueryKey.LOGIN_DIALOG]: LoginFormCopy;
  [SharedQueryKey.NAV]: Nav;
  [SharedQueryKey.EMAIL_OPT_IN]: EmailOptIn;
}
export interface CartQueryType extends BaseSanityQueryType {
  [SharedQueryKey.CART]: Cart;
}

export interface BaseProps extends BaseSanityQueryType {
  region: string;
  lang: string;
}

export interface BasePropsWithSlug extends BaseProps {
  slug: string;
}

export interface CartBaseProps extends CartQueryType, BaseProps {}

export interface CartBasePropsWithSlug
  extends CartBaseProps,
    BasePropsWithSlug {}

/** Sanity filter to only get published data */
const excludeDrafts = '!(_id in path("drafts.**"))';

/** Sanity filter to target a document type */
const whereType = (type: DocumentType) => `_type=="${type}"`;

const announcementBarQuery: string = `*[
  ${whereType("announcementBar")} &&
  ${excludeDrafts}
][0]`;

const cartQuery: string = `*[
  ${whereType("cart")} &&
  ${excludeDrafts}
][0]`;

const footerQuery: string = `*[
  ${whereType("footer")} &&
  ${excludeDrafts}
][0]`;

const navQuery: string = `*[
  ${whereType("nav")} &&
  ${excludeDrafts}
][0]`;

const emailOptInQuery: string = `*[
  ${whereType("emailOptIn")} &&
  ${excludeDrafts}
][0]`;

const configQuery: string = `*[
  ${whereType("config")} &&
  ${excludeDrafts}
][0]`;

const getLocalizedString = <T>(key: keyof T, lang: string) =>
  `${key.toString()}["${lang}"]`;

const getSlug = <T>(key: keyof T) => `${key.toString()}.current`;

/** Sanity filter to only get one matching the <slugField>.current */
const matchSlug = <T>(slugField: keyof T, slug: string) =>
  `${getSlug<T>(slugField)} == "${slug}"`;

/** All the shared Sanity filter utilities */
export const filters = {
  excludeDrafts,
  matchSlug,
  whereType,
};

/** All the shared Sanity queries */
export const queries = {
  announcementBarQuery,
  configQuery,
  footerQuery,
  navQuery,
  emailOptInQuery,
};

export const cartQueries = {
  ...queries,
  cartQuery,
};

/** All the shared Sanity utilities */
export const utils = {
  getLocalizedString,
  getSlug,
};

export const sharedSanityQuery = (lang: string): string => `{
  "${SharedQueryKey.ANNOUNCEMENT_BAR}": ${queries.announcementBarQuery},
  "${SharedQueryKey.FOOTER}": ${queries.footerQuery},
  "${SharedQueryKey.LOGIN_DIALOG}": ${buildTranslationGroupQuery(
  lang,
  "LoginDialog",
)},
  "${SharedQueryKey.CONSENT_MANAGER}": ${buildTranslationGroupQuery(
  lang,
  "SegmentConsentManager",
)},
  "${SharedQueryKey.NAV}": ${queries.navQuery},
  "${SharedQueryKey.EMAIL_OPT_IN}": ${queries.emailOptInQuery}
}`;

export const sharedSanityCartQuery = (lang: string): string => `{
  "${SharedQueryKey.ANNOUNCEMENT_BAR}": ${queries.announcementBarQuery},
  "${SharedQueryKey.CART}": ${cartQueries.cartQuery},
  "${SharedQueryKey.FOOTER}": ${queries.footerQuery},
  "${SharedQueryKey.LOGIN_DIALOG}": ${buildTranslationGroupQuery(
  lang,
  "LoginDialog",
)},
"${SharedQueryKey.CONSENT_MANAGER}": ${buildTranslationGroupQuery(
  lang,
  "SegmentConsentManager",
)},
  "${SharedQueryKey.NAV}": ${queries.navQuery},
  "${SharedQueryKey.EMAIL_OPT_IN}": ${queries.emailOptInQuery}
}`;

/**
 * Validates that a key exists on type `T`
 *
 * Intended to be used in constructing Sanity queries, as means to achieve a level
 * of "type safety" around changes to the Sanity schema and/or custom Sanity query
 * return types.
 *
 * This affords us the ability to update our types using the `yarn type-gen`
 * command and immediately know where we need to update our queries by running
 * `yarn build`. Without the type safety afforded by this function, you would
 * most likely be left with a not so helpful error at build time.
 *
 * @template T Type to check
 *
 * @param { string } key Key to check for on type `T`
 *
 * @returns { string } The provided key
 */
export const keyof = <T>(key: keyof T) => key;
